import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { appendTransactionIdToListing, transactionLineItems } from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import {
  hasPermissionToInitiateTransactions,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { LISTING_TYPE_BUSINESS } from '../../util/types';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';
import { fetchCurrentUser, setCurrentUser, setCurrentUserHasOrders } from '../../ducks/user.duck';
import { createStripeAccount, updateStripeAccount, fetchStripeAccount, } from '../../ducks/stripeConnectAccount.duck';

const { UUID } = sdkTypes;
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Async Thunks ================ //

//////////////////
// Show Listing //
//////////////////
const showListingPayloadCreator = ({ listingId, config, isOwn = false }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const showListingRequest = () => {
    if (isOwn) {
      return sdk.ownListings.show(params);
    }

    return sdk.listings.show(params).catch(error => {
      const shouldFallbackToOwnListing =
        (error?.status === 401 || error?.status === 403) &&
        typeof sdk?.ownListings?.show === 'function';

      // Some authenticated flows (e.g. private/owner-only visibility) can reject
      // public listing reads even though own-listing read is allowed.
      if (shouldFallbackToOwnListing) {
        return sdk.ownListings.show(params);
      }

      throw error;
    });
  };

  return showListingRequest()
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const showListingThunk = createAsyncThunk(
  'ListingPage/showListing',
  showListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  return dispatch(showListingThunk({ listingId, config, isOwn })).unwrap();
};

///////////////////
// Fetch Reviews //
///////////////////
export const fetchReviewsThunk = createAsyncThunk(
  'ListingPage/fetchReviews',
  ({ listingId }, { rejectWithValue, extra: sdk }) => {
    return sdk.reviews
      .query({
        listing_id: listingId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      })
      .then(response => {
        return denormalisedResponseEntities(response);
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  return dispatch(fetchReviewsThunk({ listingId })).unwrap();
};

//////////////////////
// Fetch Time Slots //
//////////////////////

const timeSlotsRequest = createAsyncThunk(
  'ListingPage/timeSlotsRequest',
  (params, { extra: sdk }) => {
    return sdk.timeslots.query(params).then(response => {
      return denormalisedResponseEntities(response);
    });
  }
);

const fetchTimeSlotsPayloadCreator = ({ listingId, start, end, timeZone, options }, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  } else {
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  }
};

export const fetchTimeSlotsThunk = createAsyncThunk(
  'ListingPage/fetchTimeSlots',
  fetchTimeSlotsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  return dispatch(fetchTimeSlotsThunk({ listingId, start, end, timeZone, options })).unwrap();
};

//////////////////
// Send Inquiry //
//////////////////
const sendInquiryPayloadCreator = (
  { listing, message, pageSummary },
  { dispatch, getState, rejectWithValue, extra: sdk }
) => {
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    return rejectWithValue(storableError(error));
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias: 'default-bidpayment/release-1', // Use new bid process
    params: {
      listingId,
      protectedData: {
        ...pageSummary,
      },
    },
  };

  console.log("pageSummary", JSON.stringify(pageSummary));
  // return

  const amount = pageSummary?.finalBid?.bidAmount;
  if(!amount) {
    return rejectWithValue(storableError(new Error('Amount not present')));
  }


  // Get state
  const state = getState();
  const initiatedTransactions =
    state.user.currentUser?.attributes?.profile?.protectedData?.initiatedTransactions || {};

  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Update listing to include this transaction id
      appendTransactionIdToListing({
        listingId,
        transactionId,
        publicDataKey: 'initiatedTransactionIds',
      }).catch(e => {
        // Ignore failure: transaction initiation already succeeded.
        console.error('appendTransactionIdToListing failed', e);
      });


      // Update current user's protectedData.initiatedTransactions
      // Note: Keep this non-blocking relative to messaging. We'll return transactionId regardless.
      const transactionIdUuid = transactionId?.uuid;
      const listingIdUuid = listingId?.uuid;

      const initialMap = Array.isArray(initiatedTransactions)
        ? {}
        : typeof initiatedTransactions === 'object' && initiatedTransactions !== null
          ? initiatedTransactions
          : {};

      const nextIds =
        listingIdUuid && transactionIdUuid
          ? {
              ...initialMap,
              [transactionIdUuid]: {
                listingIdUuid,
                amount : amount
              },
            }
          : { ...initialMap };

      sdk.currentUser
        .updateProfile(
          {
            protectedData: {
              initiatedTransactions: nextIds,
            },
          },
          {
            expand: true,
            include: ['profileImage', 'stripeAccount'],
            'fields.image': ['variants.square-small', 'variants.square-small2x'],
          }
        )
        .then(profileResponse => {
          const entities = denormalisedResponseEntities(profileResponse);
          if (entities.length === 1) {
            dispatch(setCurrentUser(entities[0]));
          }
        })
        .catch(e => {
          // Ignore failure: inquiry was already sent.
          console.error('sdk.currentUser.updateProfile failed', e);
        });



      if (!message || !message.trim()) {
        const listingIdUuid = listingId?.uuid;
        const storageKey = pageSummary?.draftStorageKey || 'rfpDraftsV1';
        return dispatch(deleteRfpDraft(listingIdUuid, storageKey))
          .catch(() => {})
          .then(() => transactionId);
      }

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(setCurrentUserHasOrders());

        const listingIdUuid = listingId?.uuid;
        const storageKey = pageSummary?.draftStorageKey || 'rfpDraftsV1';
        return dispatch(deleteRfpDraft(listingIdUuid, storageKey))
          .catch(() => {})
          .then(() => transactionId);
      });

    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const sendInquiryThunk = createAsyncThunk(
  'ListingPage/sendInquiry',
  sendInquiryPayloadCreator
);
// Backward compatible wrapper for the thunk
export const sendInquiry = (listing, message, pageSummary = null) => (dispatch, getState, sdk) => {
  return dispatch(sendInquiryThunk({ listing, message, pageSummary })).unwrap();
};

////////////////////////
// Save RFP Draft Data //
////////////////////////
export const saveRfpDraftThunk = createAsyncThunk(
  'EventDetailsPage/saveRfpDraft',
  ({ listingId, draft, storageKey = 'rfpDraftsV1' }, { dispatch, rejectWithValue, extra: sdk }) => {
    if (!listingId) {
      return rejectWithValue(storableError(new Error('Missing listingId')));
    }

    const queryParams = {
      expand: true,
      include: ['profileImage', 'stripeAccount'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .show({ expand: false })
      .then(showResp => {
        const entities = denormalisedResponseEntities(showResp);
        const currentUser = entities.length === 1 ? entities[0] : null;
        const existingPrivateData = currentUser?.attributes?.profile?.privateData;

        const draftsMap =
          typeof existingPrivateData?.[storageKey] === 'object' && existingPrivateData?.[storageKey] !== null
            ? existingPrivateData[storageKey]
            : {};

        const nowIso = new Date().toISOString();
        const existingDraft = draftsMap?.[listingId];
        const createdAt = existingDraft?.createdAt || draft?.createdAt || nowIso;
        const nextDraft = {
          ...draft,
          createdAt,
          updatedAt: nowIso,
        };

        const nextDraftsMapBase = {
          ...draftsMap,
          [listingId]: nextDraft,
        };

        // Keep only the latest 5 drafts (evict oldest by createdAt)
        const entries = Object.entries(nextDraftsMapBase);
        const sorted = entries.sort((a, b) => {
          const aCreated = a?.[1]?.createdAt ? new Date(a[1].createdAt).getTime() : 0;
          const bCreated = b?.[1]?.createdAt ? new Date(b[1].createdAt).getTime() : 0;
          return aCreated - bCreated;
        });

        const overflow = Math.max(sorted.length - 5, 0);
        const trimmed = overflow > 0 ? sorted.slice(overflow) : sorted;
        const nextDraftsMap = trimmed.reduce((acc, [id, value]) => {
          acc[id] = value;
          return acc;
        }, {});

        const actionPayload = {
          privateData: {
            ...(typeof existingPrivateData === 'object' && existingPrivateData !== null ? existingPrivateData : {}),
            [storageKey]: nextDraftsMap,
          },
        };

        return sdk.currentUser.updateProfile(actionPayload, queryParams);
      })
      .then(updateResp => {
        const entities = denormalisedResponseEntities(updateResp);
        if (entities.length === 1) {
          dispatch(setCurrentUser(entities[0]));
        }
        return true;
      })
      .catch(e => rejectWithValue(storableError(e)));
  }
);

export const saveRfpDraft = (listingId, draft, storageKey) => dispatch => {
  return dispatch(saveRfpDraftThunk({ listingId, draft, storageKey })).unwrap();
};

//////////////////////////
// Delete RFP Draft Data //
//////////////////////////
export const deleteRfpDraftThunk = createAsyncThunk(
  'EventDetailsPage/deleteRfpDraft',
  ({ listingId, storageKey = 'rfpDraftsV1' }, { dispatch, rejectWithValue, extra: sdk }) => {
    if (!listingId) {
      return rejectWithValue(storableError(new Error('Missing listingId')));
    }

    const queryParams = {
      expand: true,
      include: ['profileImage', 'stripeAccount'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .show({ expand: false })
      .then(showResp => {
        const entities = denormalisedResponseEntities(showResp);
        const currentUser = entities.length === 1 ? entities[0] : null;
        const existingPrivateData = currentUser?.attributes?.profile?.privateData;

        const draftsMap =
          typeof existingPrivateData?.[storageKey] === 'object' && existingPrivateData?.[storageKey] !== null
            ? existingPrivateData[storageKey]
            : {};

        if (!draftsMap?.[listingId]) {
          return true;
        }

        const nextDraftsMap = { ...draftsMap };
        delete nextDraftsMap[listingId];

        const actionPayload = {
          privateData: {
            ...(typeof existingPrivateData === 'object' && existingPrivateData !== null ? existingPrivateData : {}),
            [storageKey]: nextDraftsMap,
          },
        };

        return sdk.currentUser.updateProfile(actionPayload, queryParams);
      })
      .then(updateResp => {
        if (updateResp === true) {
          return true;
        }

        const entities = denormalisedResponseEntities(updateResp);
        if (entities.length === 1) {
          dispatch(setCurrentUser(entities[0]));
        }
        return true;
      })
      .catch(e => rejectWithValue(storableError(e)));
  }
);

export const deleteRfpDraft = (listingId, storageKey) => dispatch => {
  return dispatch(deleteRfpDraftThunk({ listingId, storageKey })).unwrap();
};

// Helper function for loadData call.
// Note: listing could be ownListing entity too
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
        ? 'hour'
        : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
          extraQueryParams: {
            intervalDuration: 'P1D',
            intervalAlign,
            maxPerInterval: 1,
            minDurationStartingInInterval,
            perPage: 31,
            page: 1,
          },
        }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

//////////////////////////////////
// Fetch Transaction Line Items //
//////////////////////////////////
const fetchTransactionLineItemsPayloadCreator = (
  { orderData, listingId, isOwnListing },
  { rejectWithValue }
) => {
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      return response.data;
    })
    .catch(e => {
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransactionLineItemsThunk = createAsyncThunk(
  'ListingPage/fetchTransactionLineItems',
  fetchTransactionLineItemsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  return dispatch(fetchTransactionLineItemsThunk({ orderData, listingId, isOwnListing })).unwrap();
};

/////////////////////////
// Save Payout Details //
/////////////////////////
const savePayoutDetailsPayloadCreator = (
  { values, isUpdateCall },
  { dispatch, rejectWithValue }
) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      return response;
    })
    .catch(() => {
      return rejectWithValue();
    });
};

export const savePayoutDetailsThunk = createAsyncThunk(
  'EditListingPage/savePayoutDetails',
  savePayoutDetailsPayloadCreator
);
// Backward compatible wrappers for the thunks
////////////////////////////////
// fetchOwnBusinessListing   //
///////////////////////////////
// A provider can only be booked through their business listing: checkout runs
// against it. Resolve the current user's own one so the bid rail can require it
// before a bid is submitted, and so the bid can record its id.
//
// The profile flag (protectedData.businessListingId) is the fast path but is
// not authoritative: it is written by the signup and company-profile flows and
// can be missing on accounts that predate them. Fall back to listing the user's
// own listings and picking the business one out, so a provider who has a
// listing is never told they do not.
const fetchOwnBusinessListingPayloadCreator = async (
  _,
  { getState, rejectWithValue, extra: sdk }
) => {
  const currentUser = getState().user?.currentUser;
  if (!currentUser?.id) {
    return null;
  }
  const { businessListingId } =
    currentUser?.attributes?.profile?.protectedData || {};

  const isBusinessListing = l => {
    const pd = l?.attributes?.publicData || {};
    return (
      pd.typeOfListing === LISTING_TYPE_BUSINESS ||
      pd.listingType === 'default-accept-bid'
    );
  };

  try {
    if (businessListingId) {
      const response = await sdk.ownListings.show({ id: new UUID(businessListingId) });
      const listing = denormalisedResponseEntities(response)[0] || null;
      if (listing?.id?.uuid) {
        return listing;
      }
    }
  } catch (e) {
    // The recorded id may point at a listing that no longer exists. Fall
    // through to the query rather than failing the whole lookup.
    console.error('Own business listing show failed:', businessListingId, e);
  }

  try {
    const response = await sdk.ownListings.query({ perPage: 100 });
    const listings = denormalisedResponseEntities(response) || [];
    return listings.find(isBusinessListing) || null;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchOwnBusinessListingThunk = createAsyncThunk(
  'EventDetailsPage/fetchOwnBusinessListing',
  fetchOwnBusinessListingPayloadCreator
);

export const fetchOwnBusinessListing = () => dispatch => {
  return dispatch(fetchOwnBusinessListingThunk());
};

export const savePayoutDetails = (values, isUpdateCall) => dispatch => {
  return dispatch(savePayoutDetailsThunk({ values, isUpdateCall })).unwrap();
};

// ================ Slice ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  saveRfpDraftInProgress: false,
  saveRfpDraftError: null,
  ownBusinessListing: null,
  ownBusinessListingFetched: false,
};

const eventDetailsPageSlice = createSlice({
  name: 'EventDetailsPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(showListingThunk.pending, (state, action) => {
        state.id = action.meta.arg.listingId;
        state.showListingError = null;
      })
      .addCase(showListingThunk.fulfilled, (state, action) => {
        // Data is handled by addMarketplaceEntities in the thunk
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        state.showListingError = action.payload;
      })
      .addCase(fetchOwnBusinessListingThunk.fulfilled, (state, action) => {
        state.ownBusinessListing = action.payload || null;
        state.ownBusinessListingFetched = true;
      })
      .addCase(fetchOwnBusinessListingThunk.rejected, state => {
        state.ownBusinessListing = null;
        state.ownBusinessListingFetched = true;
      })
      .addCase(fetchReviewsThunk.pending, state => {
        state.fetchReviewsError = null;
      })
      .addCase(fetchReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(fetchReviewsThunk.rejected, (state, action) => {
        state.fetchReviewsError = action.payload;
      })
      .addCase(fetchTimeSlotsThunk.pending, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          state.timeSlotsForDate = removeOutdatedDateData(state.timeSlotsForDate);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = null;
          state.timeSlotsForDate[dateId].fetchedAt = null;
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = true;
          state.timeSlotsForDate[dateId].timeSlots = [];
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = null;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = true;
        }
      })
      .addCase(fetchTimeSlotsThunk.fulfilled, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchedAt = new Date().getTime();
          state.timeSlotsForDate[dateId].timeSlots = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].timeSlots = action.payload;
        }
      })
      .addCase(fetchTimeSlotsThunk.rejected, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = action.payload;
        }
      })
      .addCase(sendInquiryThunk.pending, state => {
        state.sendInquiryInProgress = true;
        state.sendInquiryError = null;
      })
      .addCase(sendInquiryThunk.fulfilled, state => {
        state.sendInquiryInProgress = false;
        state.inquiryModalOpenForListingId = null;
      })
      .addCase(sendInquiryThunk.rejected, (state, action) => {
        state.sendInquiryInProgress = false;
        state.sendInquiryError = action.payload;
      })
      .addCase(saveRfpDraftThunk.pending, state => {
        state.saveRfpDraftInProgress = true;
        state.saveRfpDraftError = null;
      })
      .addCase(saveRfpDraftThunk.fulfilled, state => {
        state.saveRfpDraftInProgress = false;
      })
      .addCase(saveRfpDraftThunk.rejected, (state, action) => {
        state.saveRfpDraftInProgress = false;
        state.saveRfpDraftError = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.pending, state => {
        state.fetchLineItemsInProgress = true;
        state.fetchLineItemsError = null;
      })
      .addCase(fetchTransactionLineItemsThunk.fulfilled, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.lineItems = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.rejected, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.fetchLineItemsError = action.payload;
      })
      // savePayoutDetails cases
      .addCase(savePayoutDetailsThunk.pending, state => {
        state.payoutDetailsSaveInProgress = true;
      })
      .addCase(savePayoutDetailsThunk.fulfilled, state => {
        state.payoutDetailsSaveInProgress = false;
        state.payoutDetailsSaved = true;
      })
      .addCase(savePayoutDetailsThunk.rejected, state => {
        state.payoutDetailsSaveInProgress = false;
      });
  },
});

export const { setInitialValues } = eventDetailsPageSlice.actions;

export default eventDetailsPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.eventId);
  const state = getState();
  const currentUser = state.user?.currentUser;
  const inquiryModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.inquiryModalOpenForListingId
      : null;

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null, inquiryModalOpenForListingId }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const promises = hasNoViewingRights
    ? // If user has no viewing rights, only allow fetching their own listing without reviews
    [dispatch(showListing(listingId, config, true))]
    : // For users with viewing rights, fetch the listing and the associated reviews
    [dispatch(showListing(listingId, config)), dispatch(fetchReviews(listingId))];

  return Promise.all(promises).then(response => {
    const listingResponse = response[0];
    const listing = listingResponse?.data?.data;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
    if (currentUser && currentUser.stripeAccount) {
      dispatch(fetchStripeAccount());
    }
    if (isBookingProcessAlias(transactionProcessAlias) && !hasNoViewingRights) {
      // Fetch timeSlots if the user has viewing rights.
      // This can happen parallel to loadData.
      // We are not interested to return them from loadData call.
      fetchMonthlyTimeSlots(dispatch, listing);
    }
    return response;
  });
};
